<template>
    <div class="help-center">
        <!-- Search: always on top, results replace the page body while typed -->
        <div class="help-search">
            <b-field>
                <b-input v-model="query" icon-pack="far" icon="search" size="is-medium" placeholder="Search for answers"
                         icon-right-clickable :icon-right="query ? 'times-circle' : null" @icon-right-click="query = ''"
                         aria-label="Search help articles"></b-input>
            </b-field>
        </div>

        <div class="open-tickets mb-5" v-if="openTicketCount && !searching">
            <i class="fas fa-fw fa-ticket mr-2"></i>
            <span>You have {{ openTicketCount }} open support ticket{{ openTicketCount === 1 ? '' : 's' }}.</span>
            <router-link :to="{name: 'support', params: {view: 'tickets'}}" class="ml-auto">View Your Tickets <i class="fas fa-arrow-right ml-1"></i></router-link>
        </div>

        <!-- Search results -->
        <template v-if="searching">
            <section-header :title="results.length ? 'Search Results' : 'No Matching Answers'" icon="fa-search" :count="results.length || null"/>
            <div class="help-card" v-if="results.length">
                <help-article v-for="(article, i) in results" :key="article.id" :article="article" :open="i === 0 && results.length === 1"/>
            </div>
            <div class="help-card empty" v-else>
                <p class="has-text-weight-bold">We couldn't find an answer for "{{ query }}".</p>
                <p class="meta">Try fewer or different words, browse the topics, or send us a message and our team will help.</p>
            </div>
        </template>

        <!-- One article -->
        <template v-else-if="view === 'help'">
            <div class="mb-4">
                <router-link :to="topicRoute(currentTopic)" class="back-link"><i class="far fa-arrow-left mr-2"></i>{{ currentTopic ? currentTopic.title : 'Help Center' }}</router-link>
            </div>
            <div class="help-card is-page" v-if="currentArticle">
                <help-article :article="currentArticle" page/>
            </div>
            <div class="help-card empty" v-else>
                <p class="has-text-weight-bold">That help article isn't available.</p>
                <p class="meta">It may have moved. Try searching above or browse the topics.</p>
            </div>

            <template v-if="related.length">
                <section-header title="Related Questions" icon="fa-list" class="mt-6"/>
                <div class="help-card">
                    <help-article v-for="article in related" :key="article.id" :article="article"/>
                </div>
            </template>
        </template>

        <!-- One topic -->
        <template v-else-if="view === 'topic'">
            <div class="mb-4">
                <router-link :to="{name: 'support'}" class="back-link"><i class="far fa-arrow-left mr-2"></i>Help Center</router-link>
            </div>
            <template v-if="currentTopic">
                <section-header :title="currentTopic.title" :icon="currentTopic.icon" :count="currentTopicArticles.length"/>
                <p class="topic-blurb">{{ currentTopic.blurb }}</p>
                <div class="help-card">
                    <help-article v-for="article in currentTopicArticles" :key="article.id" :article="article"/>
                </div>
            </template>
            <div class="help-card empty" v-else>
                <p class="has-text-weight-bold">That help topic isn't available.</p>
            </div>
        </template>

        <!-- Home: topics + common questions -->
        <template v-else>
            <section-header title="Browse by Topic" icon="fa-grid-2"/>
            <div class="columns is-multiline topic-grid">
                <div class="column is-6-tablet is-4-widescreen" v-for="topic in orderedTopics" :key="topic.key">
                    <router-link :to="topicRoute(topic)" class="topic-card">
                        <div class="topic-icon"><i class="fad fa-fw" :class="topic.icon"></i></div>
                        <div>
                            <div class="topic-title">{{ topic.title }}</div>
                            <div class="topic-text">{{ topic.blurb }}</div>
                            <div class="topic-count">{{ topicCounts[topic.key] }} answers</div>
                        </div>
                    </router-link>
                </div>
            </div>

            <section-header title="Common Questions" icon="fa-circle-question" class="mt-5"/>
            <div class="help-card">
                <help-article v-for="article in popular" :key="article.id" :article="article"/>
            </div>
        </template>

        <!-- Always: the way out to a person -->
        <div class="info-box is-card contact-box mt-6">
            <div class="box-main">
                <div class="box-icon"><i class="fad fa-fw fa-messages-question fa-2x"></i></div>
                <div class="box-body">
                    <div class="box-title">Still Need Help?</div>
                    <div class="box-text">Send us a message and our team will get back to you. Our reply will appear under Your Tickets.</div>
                </div>
            </div>
            <div class="box-actions">
                <div class="box-buttons">
                    <b-button tag="router-link" :to="{name: 'support', params: {view: 'tickets'}}" type="is-primary" size="is-small" outlined>
                        <i class="fas fa-fw fa-ticket mr-2"></i>Your Tickets
                    </b-button>
                    <b-button tag="router-link" :to="{name: 'support', params: {view: 'new'}}" type="is-primary" size="is-small">
                        <i class="fas fa-fw fa-paper-plane mr-2"></i>Send Us a Message
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import SectionHeader from "../framework/SectionHeader";
    import HelpArticle from "./HelpArticle";
    import {HELP_TOPICS, HELP_ARTICLES, helpArticle, helpTopic, topicArticles} from "../../constants/helpCenter";
    import {searchHelp} from "../../utils/helpSearch";

    // What students ask most (support tickets 2023–2026), shown on the home view
    const POPULAR = ['wiaa-sync', 'login-methods', 'finish-course', 'find-forms', 'reactivate', 'reflections-good', 'log-time', 'receipts', 'conference-credit', 'refunds'];

    /**
     * Support › Help Center. Routes (via Support.vue):
     *   /support                 home: topics + common questions
     *   /support/topic/:key      one topic's questions
     *   /support/help/:id        one article (staff can link these in replies)
     * Topics and articles tied to a certification the student has switched
     * off sort after their own; nothing is hidden (a new coach may not have
     * turned WIAA on yet).
     */
    export default {
        name: "HelpCenter",
        components: {SectionHeader, HelpArticle},
        props: {
            view: {type: String, default: ''},
            item: {type: String, default: null},
        },
        data() {
            return {query: ''};
        },
        computed: {
            ...mapGetters(['supportTickets', 'certificationOn', 'certificationsLoaded', 'currentUser']),
            searching() {
                return this.query.trim().length >= 2;
            },
            results() {
                return this.searching ? this.byRelevance(searchHelp(this.query, {limit: 15})) : [];
            },
            orderedTopics() {
                return this.byRelevance(HELP_TOPICS);
            },
            topicCounts() {
                const counts = {};
                HELP_ARTICLES.forEach(a => {
                    counts[a.topic] = (counts[a.topic] || 0) + 1;
                });
                return counts;
            },
            popular() {
                return this.byRelevance(POPULAR.map(helpArticle).filter(Boolean)).slice(0, 8);
            },
            currentArticle() {
                return this.view === 'help' ? helpArticle(this.item) : null;
            },
            currentTopic() {
                if (this.view === 'topic') return helpTopic(this.item);
                return this.currentArticle ? helpTopic(this.currentArticle.topic) : null;
            },
            currentTopicArticles() {
                return this.currentTopic ? topicArticles(this.currentTopic.key) : [];
            },
            related() {
                if (!this.currentArticle) return [];
                return this.currentTopicArticles.filter(a => a.id !== this.currentArticle.id);
            },
            openTicketCount() {
                return (this.supportTickets || []).filter(t => t.status !== 'closed').length;
            }
        },
        watch: {
            // Navigating (a topic card, a related link) clears a search
            '$route.fullPath'() {
                this.query = '';
            }
        },
        methods: {
            relevant(x) {
                const cert = x.certification || (helpTopic(x.topic) || {}).certification;
                return !cert || this.certificationOn(cert);
            },
            // Stable: the student's own programs first, original order kept
            byRelevance(list) {
                return [...list.filter(x => this.relevant(x)), ...list.filter(x => !this.relevant(x))];
            },
            topicRoute(topic) {
                return topic ? {name: 'support', params: {view: 'topic', item: topic.key}} : {name: 'support'};
            }
        },
        mounted() {
            if (this.currentUser && !this.certificationsLoaded) this.$store.dispatch('getCertifications').catch(() => {});
        }
    };
</script>

<style lang="scss" scoped>
    .help-center {
        padding-top: 1.5rem;
        padding-bottom: 4rem;
    }

    .help-search {
        margin-bottom: 2rem;

        ::v-deep .input {
            box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);
            border: none;
        }
    }

    .open-tickets {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.25rem;
        background: rgba(255, 255, 255, 0.12);
        color: white;
        border-radius: 4px;
        padding: 0.75rem 1rem;

        a {
            color: white;
            font-weight: 600;
        }
    }

    .back-link {
        color: #9faec2;
        font-size: 0.9rem;

        &:hover {
            color: white;
        }
    }

    .help-card {
        background: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        overflow: hidden;

        &.is-page {
            padding: 2.5rem 3rem 2.5rem;

            @media (max-width: 768px) {
                padding: 1.5rem 1rem;
            }
        }

        &.empty {
            padding: 2rem 2.5rem;

            p {
                color: #4a4a4a;
            }

            .meta {
                font-size: 0.9rem;
                color: #666d7a;
                margin-top: 0.25rem;
            }
        }
    }

    .topic-blurb {
        color: #c9d3e0;
        margin: -0.25rem 0 1rem;
    }

    .topic-grid {
        margin-bottom: 1rem;
    }

    .topic-card {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        height: 100%;
        background: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 1.25rem;
        transition: transform 150ms;

        &:hover {
            transform: translateY(-1px);

            .topic-title {
                color: #336699;
            }
        }

        .topic-icon {
            font-size: 1.6rem;
            color: #336699;
            line-height: 1;
            padding-top: 0.15rem;
        }

        .topic-title {
            color: #224C74;
            font-weight: bold;
            line-height: 1.3;
        }

        .topic-text {
            color: #4a4a4a;
            font-size: 0.85rem;
            margin-top: 0.3rem;
            line-height: 1.45;
        }

        .topic-count {
            color: #8592a6;
            font-size: 0.75rem;
            margin-top: 0.5rem;
        }
    }

    .contact-box {
        .box-icon i {
            color: #336699;
        }
    }
</style>
